import React, { useMemo, useState } from 'react';
import GradedCanvas from './GradedCanvas';
import MovieStills from './MovieStills';
import { useClipFrame, useMasterThumbs } from './ClipThumb';
import {
  DEFAULT_GRADE,
  clone,
  isIdentityGrade,
  activeMasks,
  presetLabel,
} from '../utils/grade';
import {
  fmtTime,
  clipSpeed,
  clipRealSpeed,
  clipSourceLength,
  clipLength,
  clipSpeedKeys,
  hasSpeedRamp,
  clipReframe,
  masterDuration,
  masterSegments,
  songLength,
} from '../utils/movie';
import { fmtUSD, estimatePublishCost } from '../utils/cost';
import { jobEtaLabel, estimateMasterSec, fmtDuration, useNow } from '../utils/eta';
import { OUTPUT_RESOLUTIONS, OUTPUT_ASPECT, OUTPUT_FPS, OUTPUT_QUALITY } from '../data/firestore';

// A neutral grade (no contrast profile) so the captured proxy frame previews
// byte-for-byte raw — matching LiveGradeStudio's "Original" pane.
const RAW_GRADE = { ...clone(DEFAULT_GRADE), profile: 'neutral' };

// Short wallclock like "45s" / "2m 30s".
function fmtClock(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  if (s < 90) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m}m ${r}s` : `${m}m`;
}

const sgn = (v) => `${v > 0 ? '+' : ''}${Math.round(v * 100) / 100}`;
const pct = (v) => `${Math.round(v * 100)}%`;

// Human-readable list of the non-default adjustments in a grade. Used to print
// exactly what a clip/movie look bakes in, so nothing is a surprise at publish.
function describeGrade(grade) {
  if (!grade || isIdentityGrade(grade)) return [];
  const g = grade;
  const d = DEFAULT_GRADE;
  const out = [];

  if (g.inputTransform && g.inputTransform !== d.inputTransform) out.push(`Input ${g.inputTransform}`);
  if (g.profile && g.profile !== d.profile) out.push(`Profile ${g.profile}`);
  if (g.display?.exposure) out.push(`Scene exposure ${sgn(g.display.exposure)} stop`);
  if (g.display?.tonemap && g.display.tonemap !== d.display.tonemap) out.push(`Tonemap ${g.display.tonemap}`);

  const b = g.basic || {};
  [['exposure', 'Exposure'], ['contrast', 'Contrast'], ['highlights', 'Highlights'],
    ['shadows', 'Shadows'], ['whites', 'Whites'], ['blacks', 'Blacks']]
    .forEach(([k, label]) => { if (b[k]) out.push(`${label} ${sgn(b[k])}`); });

  if (g.wb?.temp) out.push(`Temp ${sgn(g.wb.temp)}`);
  if (g.wb?.tint) out.push(`Tint ${sgn(g.wb.tint)}`);

  const p = g.presence || {};
  [['texture', 'Texture'], ['clarity', 'Clarity'], ['dehaze', 'Dehaze'],
    ['vibrance', 'Vibrance'], ['saturation', 'Saturation']]
    .forEach(([k, label]) => { if (p[k]) out.push(`${label} ${sgn(p[k])}`); });

  if (g.curve?.master) out.push('Tone curve');
  if (g.curve?.red || g.curve?.green || g.curve?.blue) out.push('RGB curves');
  if ((g.hsl || []).some((band) => band.h || band.s || band.l)) out.push('Color mixer (HSL)');
  if (['shadows', 'midtones', 'highlights', 'global'].some((z) => {
    const w = g.colorGrading?.[z];
    return w && (w.h || w.s || w.l);
  })) out.push('Color wheels');

  const e = g.effects || {};
  [['vignette', 'Vignette'], ['grain', 'Grain'], ['sharpness', 'Sharpness'],
    ['halation', 'Halation'], ['glow', 'Glow']]
    .forEach(([k, label]) => { if (e[k]) out.push(`${label} ${e[k]}`); });

  const masks = activeMasks(g);
  if (masks.length) out.push(`${masks.length} local mask${masks.length > 1 ? 's' : ''}`);

  return out;
}

function songName(s) {
  return s.title || s.label || s.name || (s.url ? s.url.split('/').pop() : 'Song');
}

function Chips({ items, empty = 'none' }) {
  if (!items || !items.length) return <span className="cs-tree-muted">{empty}</span>;
  return (
    <span className="cs-tree-chips">
      {items.map((s, i) => <span key={i} className="cs-tree-chip">{s}</span>)}
    </span>
  );
}

const r2 = (v) => Math.round(v * 100) / 100;

// One clip = one dense table row. Captures a representative frame (mid-trim)
// for the tiny thumbnail; expanding the row renders ONLY the Raw vs Graded
// compare (final look = clip grade baked first, movie grade on top) through the
// same GPU LUT pipeline as the studio — a WebGL context is only spent while
// open. All the parameter detail lives in the cells + their tooltips.
function ClipTableRow({ ci, open, onToggle, movieGrade, movieGradeLabel, onOpenClip, masterFrame, masterRaw, onGrabStill, grabbing }) {
  // Primary: grab the clip's OWN proxy → gives a true RAW frame (+ live graded
  // compare). Fallback: a frame from the composed master for clips whose proxy
  // can't be grabbed client-side. On a RAW master the master frame carries no
  // baked look, so we apply the clip look live too; older baked masters already
  // have the per-clip look, so we only add the movie grade.
  const perClip = useClipFrame(ci.clip, 0.5);
  const hasPerClip = perClip.status === 'ready' && !!perClip.rawUrl;
  const headRaw = hasPerClip ? perClip.rawUrl : (masterFrame?.rawUrl || '');
  const failed = perClip.status === 'error' && !masterFrame;
  const gradedBase = ci.raw ? RAW_GRADE : (ci.clip.grade || DEFAULT_GRADE);
  const gradedTop = movieGrade || DEFAULT_GRADE;
  // Clip look to apply under the movie grade on a raw master-frame fallback.
  const masterClipBase = masterRaw && !ci.raw && ci.clip.grade && !isIdentityGrade(ci.clip.grade)
    ? ci.clip.grade
    : undefined;

  // Speed cell — same ramp "set of speeds" readout as the Assemble clip list:
  // key speeds in order, consecutive duplicates collapsed (e.g. "1→4.76→8×").
  const speedSet = [];
  for (const k of ci.speedKeys) {
    const sv = r2(k.speed);
    if (!speedSet.length || Math.abs(speedSet[speedSet.length - 1] - sv) > 0.001) speedSet.push(sv);
  }
  const speedTxt = ci.ramped
    ? `${speedSet.join('→')}×`
    : Math.abs(ci.spd - 1) < 1e-3 ? '1×' : `${r2(ci.spd)}×`;
  const speedTitle = (ci.ramped
    ? `Speed ramp ${ci.speedKeys.map((k) => `@${k.t}s src → ${r2(k.speed)}×`).join(' · ')} → ${fmtTime(ci.tlLen)} on timeline`
    : Math.abs(ci.spd - 1) < 1e-3 ? 'Normal speed (1×)' : `${r2(ci.spd)}× → ${fmtTime(ci.tlLen)} on timeline`)
    + (ci.real ? ` · ${ci.real}× = real motion` : '');

  // Look cell + tooltip carrying the full adjustment list.
  const lookTxt = ci.raw ? 'raw' : (ci.lookPreset || ((ci.look && ci.look.length) ? 'graded' : '—'));
  const lookTitle = ci.raw
    ? 'Publishes raw — the saved look is switched off'
    : ci.lookPreset
      ? `${ci.lookPreset}${ci.look && ci.look.length ? ` — ${ci.look.join(', ')}` : ' (full preset)'}`
      : (ci.look && ci.look.length) ? ci.look.join(', ') : 'No clip look (neutral)';

  // Crop/zoom cell.
  const zoom = ci.rf.zoomKeys && ci.rf.zoomKeys.length > 0
    ? (() => {
      const zs = ci.rf.zoomKeys.map((k) => 1 / k.scale);
      return `${r2(Math.min(...zs))}–${r2(Math.max(...zs))}×`;
    })()
    : ci.rf.manualCrop.scale < 0.999 ? `${r2(1 / ci.rf.manualCrop.scale)}×` : '';
  const cropTxt = ci.reEdited
    ? `${ci.rf.cropMode === 'manual' ? 'manual' : `subj·${ci.rf.waypoints.length}wp`}${zoom ? ` ${zoom}` : ''}`
    : '—';
  const cropTitle = ci.reEdited
    ? (ci.rf.cropMode === 'manual'
      ? `Manual crop · center ${pct(ci.rf.manualCrop.cx)},${pct(ci.rf.manualCrop.cy)} · zoom ${r2(1 / ci.rf.manualCrop.scale)}× (crop ${pct(ci.rf.manualCrop.scale)})`
      : `Subject tracking · ${ci.rf.waypoints.length} center waypoint${ci.rf.waypoints.length === 1 ? '' : 's'}${ci.rf.subjectTrackId ? ' · subject locked' : ''}${ci.rf.zoomKeys.length ? ` · ${ci.rf.zoomKeys.length} zoom keys ${zoom}` : ''}`)
    : 'No reframe — full frame';

  const trimTxt = ci.trimmed
    ? `${fmtTime(ci.clip.inSec || 0)}→${fmtTime((ci.clip.inSec || 0) + ci.srcLen)}`
    : 'full';
  const trimTitle = ci.trimmed
    ? `Trim ${fmtTime(ci.clip.inSec || 0)} → ${fmtTime((ci.clip.inSec || 0) + ci.srcLen)} · ${ci.srcLen.toFixed(2)}s of source used`
    : `Full source (${ci.srcLen.toFixed(2)}s)`;

  return (
    <>
      <div
        className={`cs-revrow${open ? ' open' : ''}`}
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        title="Click to compare Raw vs Graded (final) frames"
      >
        <span className="cs-tree-caret">{open ? '▾' : '▸'}</span>
        <span className="cs-revrow-thumb">
          {headRaw ? <img src={headRaw} alt="" draggable={false} /> : <span className={failed ? 'cs-tree-thumb-x' : 'cs-spinner'}>{failed ? '—' : ''}</span>}
        </span>
        <span className="cs-revrow-n">{ci.idx + 1}</span>
        <span className="cs-revrow-name" title={ci.label}>{ci.label}</span>
        <span className="cs-revrow-cell" title={`Starts at ${fmtTime(ci.tlStart)} · ${fmtTime(ci.tlLen)} on the timeline`}>
          {fmtTime(ci.tlStart)}<i>+{fmtTime(ci.tlLen)}</i>
        </span>
        <span className={`cs-revrow-cell${ci.trimmed ? '' : ' dim'}`} title={trimTitle}>{trimTxt}</span>
        <span className={`cs-revrow-cell${(ci.ramped || Math.abs(ci.spd - 1) > 1e-3) ? ' speed' : ' dim'}`} title={speedTitle}>
          {speedTxt}{ci.real ? <i title={`${ci.real}× plays this slow-mo source at real-life motion`}>⏱</i> : null}
        </span>
        <span className={`cs-revrow-cell${lookTxt === '—' ? ' dim' : ci.raw ? ' dim' : ' look'}`} title={lookTitle}>{lookTxt}</span>
        <span
          className={`cs-revrow-mg${movieGradeLabel ? ' on' : ''}`}
          title={movieGradeLabel ? `Movie grade "${movieGradeLabel}" layered on top of this clip` : 'No movie-wide grade'}
        >●</span>
        <span className={`cs-revrow-cell${ci.reEdited ? ' crop' : ' dim'}`} title={cropTitle}>{cropTxt}</span>
        <span className={`cs-revrow-cell${ci.dissolve > 0 ? '' : ' dim'}`} title={ci.dissolve > 0 ? `Cross-dissolve ${ci.dissolve}s into the next clip` : 'Hard cut into the next clip'}>
          {ci.dissolve > 0 ? `⇄${ci.dissolve}s` : '—'}
        </span>
        <span className="cs-revrow-acts" onClick={(e) => e.stopPropagation()}>
          <button className="cs-revrow-ico" title="Edit trim / speed / order in Assemble" onClick={() => onOpenClip && onOpenClip(ci.clip.id, 'assemble')}>🎬</button>
          <button className="cs-revrow-ico" title="Edit this clip's look in Color & Light" onClick={() => onOpenClip && onOpenClip(ci.clip.id, 'color')}>🎨</button>
          <button className="cs-revrow-ico" title="Edit the crop / subject tracking in Reframe" onClick={() => onOpenClip && onOpenClip(ci.clip.id, 'reframe')}>🎯</button>
          {onGrabStill && ci.clip.assetId && ci.clip.tripId && (
            <button
              className="cs-revrow-ico"
              disabled={grabbing}
              onClick={() => onGrabStill(ci.clip.id)}
              title="Grab an editable still from this clip (its color/light look + crop/zoom), saved as a photo you can export as JPEG / TIFF / WebP"
            >
              {grabbing ? '⏳' : '📸'}
            </button>
          )}
        </span>
      </div>

      {open && (
        <div className="cs-revrow-body">
          {hasPerClip && perClip.canvas ? (
            <div className="cs-tree-compare">
              <figure>
                <img src={perClip.rawUrl} alt="raw" draggable={false} />
                <figcaption>Raw</figcaption>
              </figure>
              <figure>
                <GradedCanvas source={perClip.canvas} grade={gradedTop} baseGrade={gradedBase} maxWidth={320} style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 6 }} />
                <figcaption>Graded (final)</figcaption>
              </figure>
            </div>
          ) : masterFrame?.canvas ? (
            <div className="cs-tree-compare" style={{ gridTemplateColumns: '1fr', maxWidth: 260 }}>
              <figure>
                <GradedCanvas source={masterFrame.canvas} grade={gradedTop} baseGrade={masterClipBase} maxWidth={320} style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 6 }} />
                <figcaption>Final (from preview master)</figcaption>
              </figure>
            </div>
          ) : (
            <div className="cs-tree-compare"><div className="cs-tree-compare-ph"><span className={failed ? 'cs-tree-thumb-x' : 'cs-spinner'}>{failed ? 'no preview' : ''}</span></div></div>
          )}
        </div>
      )}
    </>
  );
}

// Final review + export stage. Renders the full tree of edits — every clip's
// trim / speed / look / crop plus the movie-wide grade, soundtrack and output —
// so the user can confirm everything before kicking off a compute-heavy publish
// or social export.
export default function ReviewExport({
  movie,
  movieId,
  clips = [],
  songs = [],
  movieGrade,
  intro = 0,
  outro = 0,
  hasOriginalAudio = true,
  output,
  unpublished = false,
  rendering = false,
  onPublish,
  master = {},
  masterMissing = false,
  masterStale = false,
  masterBuilding = false,
  onBuildPreview,
  reframe = {},
  clipReframes = {},
  exports = [],
  pubCost = null,
  onGoTab,
  onOpenClip,
  onGrabStill,
  grabLoadingId,
  onOutput,
}) {
  const [openClips, setOpenClips] = useState(() => new Set());
  const toggleClip = (cid) =>
    setOpenClips((prev) => {
      const n = new Set(prev);
      if (n.has(cid)) n.delete(cid); else n.add(cid);
      return n;
    });
  const allOpen = openClips.size === clips.length && clips.length > 0;
  const toggleAll = () => setOpenClips(allOpen ? new Set() : new Set(clips.map((c) => c.id)));
  // Export settings are set once per publish — collapsed to a one-line summary
  // by default so the review surface leads with the actual edit data.
  const [exportOpen, setExportOpen] = useState(false);

  const total = useMemo(() => masterDuration(clips), [clips]);
  const masterEtaSec = useMemo(() => estimateMasterSec(total), [total]);
  const masterNow = useNow(masterBuilding);
  const movieLook = describeGrade(movieGrade);
  const movieLookPreset = presetLabel(movieGrade);
  const hasMovieGrade = !!movieLookPreset || (movieLook && movieLook.length > 0);

  // Reliable thumbnail fallback: one frame per clip from the single composed
  // master MP4. Used when a clip's own proxy can't be grabbed client-side.
  const segs = useMemo(() => masterSegments(clips), [clips]);
  const masterUrl = master?.status === 'ready' ? master.url : '';
  const masterRaw = master?.raw === true;
  const { frames: masterThumbs } = useMasterThumbs(masterUrl, segs);

  // RED clips drive the heavy RAW re-decode fan-out; isolate them for per-option
  // cost estimates so each export version's price/time can be shown side by side.
  const redClips = useMemo(() => clips.filter((c) => c.isRawVideo), [clips]);
  const hasHiResOriginal = useMemo(
    () => clips.some((c) => !c.isRawVideo && (c.s3Key || c.assetId) && ((c.height || 0) > 1080 || c.height == null)),
    [clips]
  );

  // Rough encode-time multipliers (estimate only). Higher resolutions and the
  // near-lossless "max" quality push more pixels/bits through the same encoder;
  // "Max" pulls each clip's full negative (RED RAW re-decode + 5K originals).
  const resTimeFactor = (h) => (h === 0 ? 4 : h >= 2160 ? 2.6 : h >= 1440 ? 1.6 : 1);
  const qualTimeFactor = (q) => (q === 'max' ? 2 : q === 'high' ? 1.4 : 1);

  // Estimate { usd, sec, wantRaw } for an arbitrary output recipe. Cash cost is
  // dominated by the RED RAW fan-out (billed per-second tasks); the composite +
  // hi-res originals run on the always-on box, so they mostly cost wallclock.
  const estimateFor = (out) => {
    const pc = estimatePublishCost({ durationSec: total, output: out }, redClips);
    const h = out.height == null ? 1080 : Number(out.height);
    const composite = total * 1.2 * resTimeFactor(h) * qualTimeFactor(out.quality || 'standard');
    const fanout = pc.wantRaw && pc.jobs?.length
      ? Math.max(0, ...pc.jobs.map((j) => j.wallclockSec || 0))
      : 0;
    return { usd: pc.usd, sec: composite + fanout, wantRaw: pc.wantRaw };
  };

  const estRenderSec = useMemo(() => estimateFor(output || {}).sec, [total, redClips, output]); // eslint-disable-line react-hooks/exhaustive-deps

  // Export presets — the common "intentions". Each is costed live below so the
  // owner can publish exactly the version they want for a given medium.
  const presets = useMemo(() => ([
    { id: 'web', label: 'Web / social', sub: '1080p · 30 fps · standard', note: 'Fast, light. Best for Instagram/web sharing.', out: { height: 1080, fps: 30, quality: 'standard', redSource: 'proxy', prores: false } },
    { id: 'uhd', label: '4K UHD', sub: '2160p · source fps · high', note: 'Crisp 4K from the original 5K / RED detail.', out: { height: 2160, fps: 0, quality: 'high', redSource: 'proxy', prores: false } },
    { id: 'max', label: 'Max — native', sub: 'original res · all frames · near-lossless', note: 'Full negative: RED RAW re-decode + 5K originals. Heaviest.', out: { height: 0, fps: 0, quality: 'max', redSource: 'raw', prores: !!(output || {}).prores } },
  ]), [output]);

  const sameOutput = (a, b) =>
    (a.height ?? 1080) === (b.height ?? 1080) &&
    (a.fps ?? 30) === (b.fps ?? 30) &&
    (a.quality || 'standard') === (b.quality || 'standard');

  // Per-clip summaries (computed once).
  const clipInfo = useMemo(() => {
    let acc = 0;
    return clips.map((c, i) => {
    const spd = clipSpeed(c);
    const real = clipRealSpeed(c);
    const ramped = hasSpeedRamp(c);
    const speedKeys = clipSpeedKeys(c);
    const look = c.useGrade === false ? null : describeGrade(c.grade);
    const lookPreset = c.useGrade === false ? null : presetLabel(c.grade);
    const rf = clipReframe(c);
    const reEdited = !!(c.reframe) && (rf.cropMode === 'manual' || rf.subjectTrackId || rf.waypoints.length || rf.manualCrop.scale < 0.999 || (rf.zoomKeys && rf.zoomKeys.length > 0));
    const tlLen = clipLength(c);
    const tlStart = acc;
    acc += tlLen;
    return {
      clip: c,
      idx: i,
      label: c.label || `Clip ${i + 1}`,
      srcLen: clipSourceLength(c),
      tlLen,
      tlStart,
      spd,
      real,
      ramped,
      speedKeys,
      trimmed: (c.inSec || 0) > 0.01 || (!!c.outSec && c.outSec > 0 && c.outSec < (c.sourceDurationSec || Infinity) - 0.01),
      look,
      lookPreset,
      raw: c.useGrade === false,
      rf,
      reEdited,
      dissolve: c.dissolveSec || 0,
    };
    });
  }, [clips]);

  const stats = useMemo(() => ({
    graded: clipInfo.filter((c) => (c.look && c.look.length) || c.lookPreset).length,
    raw: clipInfo.filter((c) => c.raw).length,
    retimed: clipInfo.filter((c) => Math.abs(c.spd - 1) > 1e-3).length,
    reframed: clipInfo.filter((c) => c.reEdited).length,
    dissolves: clipInfo.filter((c) => c.dissolve > 0).length,
  }), [clipInfo]);

  // Subject tracking is per-clip now: a subject-mode clip is "analyzed" if it has
  // its own ready clipReframe doc, or (legacy) the movie-wide analysis is ready.
  const subjectClips = clips.filter((c) => {
    const r = clipReframe(c);
    return r.cropMode === 'subject' && (r.subjectTrackId || (r.waypoints || []).length);
  });
  const legacyReady = reframe.status === 'ready';
  const reMissing = subjectClips.some((c) => !(clipReframes[c.id]?.status === 'ready') && !legacyReady);
  const reStale = reframe.status === 'stale' && !Object.keys(clipReframes).length;

  // Readiness checklist.
  const checks = [];
  if (!clips.length) checks.push({ level: 'err', msg: 'No clips in this movie yet.' });
  if (unpublished) checks.push({ level: 'info', msg: 'You have unpublished edits — Publish to render the shareable stream.' });
  if (masterMissing) checks.push({ level: 'warn', msg: 'Preview master not built — needed for Reframe & social export.', fix: onBuildPreview, fixLabel: `Build preview · ~${fmtDuration(masterEtaSec)}` });
  else if (masterStale) checks.push({ level: 'warn', msg: 'Preview master is out of date — behind the current cut (speed/trim/order changed).', fix: onBuildPreview, fixLabel: `Rebuild preview · ~${fmtDuration(masterEtaSec)}` });
  if (reMissing) checks.push({ level: 'warn', msg: 'Some clips use subject tracking but haven’t been analyzed yet — crops fall back to center. Analyze them per clip in Reframe.', fix: () => onGoTab && onGoTab('reframe'), fixLabel: 'Open Reframe' });
  else if (reStale) checks.push({ level: 'warn', msg: 'Subject analysis is stale — re-analyze for accurate tracking.', fix: () => onGoTab && onGoTab('reframe'), fixLabel: 'Open Reframe' });
  const ready = !checks.some((c) => c.level === 'err' || c.level === 'warn');

  const geom = output || {};
  const songsUsable = (songs || []).filter((s) => s.source === 'upload');

  return (
    <div className="cs-review">
      {/* Readiness header + the heavy actions, gated behind the checklist. */}
      <div className={`cs-review-status ${ready ? 'ok' : 'warn'}`}>
        <div className="cs-review-status-main">
          <strong>{ready ? '✓ Ready to publish' : 'Review before publishing'}</strong>
          <span className="cs-help">
            {clips.length} clip{clips.length === 1 ? '' : 's'} · {fmtTime(total)} · {stats.graded} graded ·
            {' '}{stats.retimed} retimed · {stats.reframed} reframed
          </span>
        </div>
        <div className="cs-review-status-actions">
          {masterBuilding ? (
            <span className="cs-help" style={{ fontSize: 12 }}>
              {jobEtaLabel(master, { verb: 'Building preview', estSec: masterEtaSec, nowMs: masterNow })}
            </span>
          ) : (masterMissing || masterStale) ? (
            <button
              className="cs-btn cs-btn-ghost cs-btn-sm"
              onClick={onBuildPreview}
              title={`${masterMissing ? 'Build' : 'Rebuild'} the preview master (~${fmtDuration(masterEtaSec)})`}
            >
              ↻ {masterMissing ? 'Build' : 'Rebuild'} preview · ~{fmtDuration(masterEtaSec)}
            </button>
          ) : null}
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => onGoTab && onGoTab('reframe')}>
            Social / vertical export →
          </button>
          <div
            className="cs-review-est"
            title={`Rough estimate. The HLS ladder renders on the always-on backend (~1.2× realtime${pubCost?.wantRaw ? '; RED RAW re-decode fans out to per-second billed tasks' : ', ~no extra charge'}).`}
          >
            <span className="cs-review-est-time">~{fmtClock(estRenderSec)}</span>
            <span className="cs-review-est-cost">{fmtUSD(pubCost?.usd || 0)}</span>
          </div>
          <button className="cs-btn cs-btn-primary" disabled={!clips.length || rendering} onClick={onPublish}>
            {rendering ? 'Publishing…' : unpublished ? 'Publish stream' : 'Republish stream'}
          </button>
        </div>
      </div>

      {checks.length > 0 && (
        <ul className="cs-review-checks">
          {checks.map((c, i) => (
            <li key={i} className={`cs-review-check ${c.level}`}>
              <span className="cs-review-dot" />
              <span style={{ flex: '1 1 auto' }}>{c.msg}</span>
              {c.fix && <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={c.fix}>{c.fixLabel}</button>}
            </li>
          ))}
        </ul>
      )}

      {/* Export settings — choose the publish version intentionally; each option
          is costed (time + $). Collapsed to a one-line summary of the current
          recipe by default; expand to change it. */}
      <div className="cs-export">
        <div
          className="cs-export-head cs-export-head-toggle"
          role="button"
          tabIndex={0}
          onClick={() => setExportOpen((o) => !o)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExportOpen((o) => !o); } }}
          title={exportOpen ? 'Collapse export settings' : 'Change resolution / fps / quality — each option is costed (time + $)'}
        >
          <span className="cs-tree-caret">{exportOpen ? '▾' : '▸'}</span>
          <span className="cs-export-title">Export settings</span>
          <span className="cs-export-summary">
            <span className="cs-tree-chip">{OUTPUT_RESOLUTIONS.find((r) => r.value === (geom.height ?? 1080))?.label || `${geom.height}p`}</span>
            <span className="cs-tree-chip">{OUTPUT_ASPECT.find((a) => a.value === (geom.aspect || 'source'))?.label || geom.aspect}</span>
            <span className="cs-tree-chip">{OUTPUT_FPS.find((f) => f.value === (geom.fps ?? 30))?.label || `${geom.fps} fps`}</span>
            <span className="cs-tree-chip">{OUTPUT_QUALITY.find((q) => q.value === (geom.quality || 'standard'))?.label || geom.quality}</span>
            {geom.prores ? <span className="cs-tree-chip">+ ProRes master</span> : null}
          </span>
          <span className="cs-export-summary-est">
            <span className="cs-export-time">~{fmtClock(estRenderSec)}</span>
            <span className="cs-export-cost">{estimateFor(output || {}).usd > 0.005 ? fmtUSD(estimateFor(output || {}).usd) : 'no extra $'}</span>
          </span>
        </div>

        {exportOpen && (
        <>
        <div className="cs-help" style={{ margin: '2px 0 8px' }}>
          Editing always uses light proxies for speed. Pick exactly how this film should publish — you can republish at a different version anytime.
        </div>

        <div className="cs-export-presets">
          {presets.map((p) => {
            const est = estimateFor(p.out);
            const active = sameOutput(output || {}, p.out);
            return (
              <button
                type="button"
                key={p.id}
                className={`cs-export-preset ${active ? 'active' : ''}`}
                onClick={() => onOutput && onOutput(p.out)}
                disabled={!onOutput}
                title={p.note}
              >
                <span className="cs-export-preset-top">
                  <span className="cs-export-preset-name">{p.label}</span>
                  {active && <span className="cs-export-preset-on">selected</span>}
                </span>
                <span className="cs-export-preset-sub">{p.sub}</span>
                <span className="cs-export-preset-est">
                  <span className="cs-export-time">~{fmtClock(est.sec)}</span>
                  <span className="cs-export-cost">{est.usd > 0.005 ? fmtUSD(est.usd) : 'no extra $'}</span>
                </span>
                <span className="cs-export-preset-note">{p.note}</span>
              </button>
            );
          })}
        </div>

        <div className="cs-export-custom">
          <span className="cs-export-custom-label">Custom</span>
          <label className="cs-export-field">
            <span>Resolution</span>
            <select
              value={(output || {}).height ?? 1080}
              disabled={!onOutput}
              onChange={(e) => onOutput && onOutput({ height: Number(e.target.value) })}
            >
              {OUTPUT_RESOLUTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </label>
          <label className="cs-export-field">
            <span>Aspect ratio</span>
            <select
              value={(output || {}).aspect || 'source'}
              disabled={!onOutput}
              onChange={(e) => onOutput && onOutput({ aspect: e.target.value })}
            >
              {OUTPUT_ASPECT.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </label>
          <label className="cs-export-field">
            <span>Frame rate</span>
            <select
              value={(output || {}).fps ?? 30}
              disabled={!onOutput}
              onChange={(e) => onOutput && onOutput({ fps: Number(e.target.value) })}
            >
              {OUTPUT_FPS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </label>
          <label className="cs-export-field">
            <span>Quality</span>
            <select
              value={(output || {}).quality || 'standard'}
              disabled={!onOutput}
              onChange={(e) => onOutput && onOutput({ quality: e.target.value })}
            >
              {OUTPUT_QUALITY.map((q) => <option key={q.value} value={q.value}>{q.label}</option>)}
            </select>
          </label>
          <label className="cs-export-check" title="Also write a ProRes 422 HQ archival master alongside the H.264 download.">
            <input
              type="checkbox"
              checked={!!(output || {}).prores}
              disabled={!onOutput}
              onChange={(e) => onOutput && onOutput({ prores: e.target.checked })}
            />
            <span>ProRes master</span>
          </label>
          <div className="cs-export-custom-est">
            <span className="cs-export-time">~{fmtClock(estRenderSec)}</span>
            <span className="cs-export-cost">{estimateFor(output || {}).usd > 0.005 ? fmtUSD(estimateFor(output || {}).usd) : 'no extra $'}</span>
          </div>
        </div>

        <div className="cs-help cs-export-foot">
          {(output || {}).height === 0
            ? `Max: each clip publishes at its native resolution and full frame rate — RED clips re-decode from the .R3D negative${hasHiResOriginal ? ' and 5K originals are read at full detail' : ''}. Heaviest in time${redClips.length ? ' and the only option with extra $ (per-second RAW tasks)' : ''}.`
            : (output || {}).height > 1080
              ? `Renders above 1080p pull each clip's original file (incl. 5K) instead of the edit proxy. Runs on the always-on backend — mainly costs time, ~no extra $.`
              : `1080p publishes straight from the edit proxies — fastest and ~no extra $. Bump resolution above for full 5K / RED detail.`}
          {' '}fps "Match source" keeps every frame as shot (your RED footage is ~23.976).
        </div>
        </>
        )}
      </div>

      {/* ---- Movie-wide strip: output / grade / soundtrack / exports as dense
           inline chip-group lines instead of stacked node blocks. ---- */}
      <div className="cs-tree-card cs-rev-movie">
        <div className="cs-tree-card-head">
          <h3>Movie</h3>
          <span className="cs-help">{movie?.title || 'Untitled'}</span>
        </div>
        <div className="cs-rev-line">
          <label>Output</label>
          <span className="cs-tree-chip">{geom.w && geom.h ? `${geom.w}×${geom.h}` : '1080p'}</span>
          <span className="cs-tree-chip">{geom.fps || movie?.render?.outputFps || 30} fps</span>
          <span className="cs-tree-chip">⏱ {fmtTime(total)}</span>
          {intro || outro
            ? <span className="cs-tree-chip" title="Movie-level fade to black">{intro ? `fade-in ${intro}s` : ''}{intro && outro ? ' · ' : ''}{outro ? `fade-out ${outro}s` : ''}</span>
            : <span className="cs-tree-muted">no intro/outro fades</span>}
          <button className="cs-linklike" onClick={() => onGoTab && onGoTab('details')}>edit</button>
        </div>
        <div className="cs-rev-line">
          <label>Grade</label>
          <span className={`cs-tree-badge ${hasMovieGrade ? 'look' : 'muted'}`}>
            {hasMovieGrade ? (movieLookPreset || 'custom') : 'none'}
          </span>
          {hasMovieGrade ? (
            movieLookPreset && !movieLookPreset.includes('· edited')
              ? <span className="cs-tree-muted">full preset, applied on top of each clip’s own look</span>
              : <Chips items={movieLook} empty="neutral" />
          ) : (
            <span className="cs-tree-muted">each clip publishes with only its own look (or raw)</span>
          )}
          <button className="cs-linklike" onClick={() => onGoTab && onGoTab('color')}>edit</button>
        </div>
        <div className="cs-rev-line">
          <label>Sound</label>
          <span className="cs-tree-chip" title={hasOriginalAudio ? 'Clip audio kept, mixed under the music' : 'Clip audio muted'}>
            clip audio {hasOriginalAudio ? 'kept' : 'muted'}
          </span>
          {songsUsable.length ? songsUsable.map((s, i) => (
            <span key={i} className="cs-tree-chip" title={`Song ${i + 1}`}>
              ♪ {songName(s)} · {fmtTime(songLength(s))}{s.loop ? ' · ⟳' : ''}
              {s.volume != null && s.volume !== 1 ? ` · vol ${pct(s.volume)}` : ''}
              {s.fadeInSec ? ` · in ${s.fadeInSec}s` : ''}
              {s.fadeOutSec ? ` · out ${s.fadeOutSec}s` : ''}
              {s.movieOffset ? ` · @ ${fmtTime(s.movieOffset)}` : ''}
            </span>
          )) : <span className="cs-tree-muted">no songs</span>}
          <button className="cs-linklike" onClick={() => onGoTab && onGoTab('assemble')}>edit</button>
        </div>
        {exports && exports.length > 0 && (
          <div className="cs-rev-line">
            <label>Exports</label>
            <span className="cs-rev-exports">
              {exports.slice(0, 4).map((ex) => (
                <span key={ex.id}>
                  <b>{ex.label || ex.id.slice(0, 6)}</b> — {(ex.presets || []).join(', ') || '—'} · {ex.status}
                  {ex.status === 'queued' || ex.status === 'rendering' ? ` ${ex.progress || 0}%` : ''}
                </span>
              ))}
            </span>
          </div>
        )}
      </div>

      {/* ---- Per-clip table: one dense row per clip; expand for the Raw vs
           Graded compare. ---- */}
      <div className="cs-tree-card cs-rev-clips">
        <div className="cs-tree-card-head">
          <h3>Clips <span className="cs-tree-muted">({clips.length})</span></h3>
          <button className="cs-linklike" onClick={toggleAll}>{allOpen ? 'collapse all' : 'expand all'}</button>
        </div>

        {clips.length > 0 && (
          <div className="cs-revrow cs-revrow-head" aria-hidden="true">
            <span />
            <span />
            <span className="cs-revrow-n">#</span>
            <span className="cs-revrow-name">clip</span>
            <span className="cs-revrow-cell">timeline</span>
            <span className="cs-revrow-cell">trim</span>
            <span className="cs-revrow-cell">speed</span>
            <span className="cs-revrow-cell">look</span>
            <span className="cs-revrow-mg" title="Movie-wide grade layered on top">m</span>
            <span className="cs-revrow-cell">crop</span>
            <span className="cs-revrow-cell">⇄</span>
            <span className="cs-revrow-acts">edit</span>
          </div>
        )}
        {clipInfo.map((ci) => (
          <ClipTableRow
            key={ci.clip.id}
            ci={ci}
            open={openClips.has(ci.clip.id)}
            onToggle={() => toggleClip(ci.clip.id)}
            movieGrade={movieGrade}
            movieGradeLabel={hasMovieGrade ? (movieLookPreset || 'custom') : null}
            onOpenClip={onOpenClip}
            masterFrame={masterThumbs[ci.clip.id]}
            masterRaw={masterRaw}
            onGrabStill={onGrabStill}
            grabbing={grabLoadingId === ci.clip.id}
          />
        ))}
        {!clips.length && <div className="cs-empty">No clips yet — add footage in Assemble.</div>}
      </div>

      <MovieStills movieId={movieId || movie?.id || ''} clips={clips} />
    </div>
  );
}
